'use client';
import { Button } from '@curology/ui-components-web/react/Button';
import { Progress } from '@curology/ui-components-web/react/Progress';
import React, { useEffect, useState } from 'react';

import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import { BRAND_IDS } from '@/consts/brands';
import { TreatmentCategoryIds } from '@/consts/treatmentCategories';
import { type PhotoResource } from '@/services/funnelService/types';
import { type AnalyticsPatientResource } from '@/types/metrics/analyticsPatient';
import { AnalyticsEvent, trackEvent } from '@/utils/analytics';

import { PhotoIdModal } from './components/photoIdModal';
import PhotoUploadAccordion from './components/photoUploadAccordion';
import SmsLink from './components/smsLink';
import { VerifyPhoneNumberModal } from './components/verifyPhoneNumberModal';
import WeAreSorryMessage from './components/weAreSorryMessage';
import { type PhotoUploadFile } from './types/types';
import { getRequiredPhotoTypes, initPageData } from './utils/helpers';
import { type QuestionPhotoUploadModel } from '../../config/questions/photoUploadQuestion';

const PhotoUpload = ({ question }: { question: QuestionPhotoUploadModel }) => {
  const [patient, setPatient] = useState<
    AnalyticsPatientResource | undefined
  >();

  const [phoneNumber, setPhoneNumber] = useState('');
  const [photos, setPhotos] = useState<PhotoResource[]>([]);
  const [showPhotoIdModal, setShowPhotoIdModal] = useState(false);

  const [showVerifyPhoneNumberModal, setShowVerifyPhoneNumberModal] =
    useState(false);

  const [showWeAreSorryMessage, setShowWeAreSorryMessage] = useState(false);
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(true);
  const [uploadedFiles, setUploadedFiles] = useState<PhotoUploadFile[]>([]);

  const { flowType, treatmentCategory, brand } = useFlowContext();
  const brandId = BRAND_IDS[brand];

  useEffect(() => {
    if (treatmentCategory) {
      const init = async () => {
        const response = await initPageData(
          question,
          setPatient,
          setPhotos,
          setShowWeAreSorryMessage,
          setSubmitButtonDisabled,
          setUploadedFiles,
          brand,
          treatmentCategory,
          flowType,
        );

        question.value = response;
      };

      init();
    }
  }, [question, brand, treatmentCategory, flowType]);

  const requiredPhotoTypes = getRequiredPhotoTypes(photos);

  if (showWeAreSorryMessage) {
    return <WeAreSorryMessage />;
  }

  if (photos.length === 0) {
    return (
      <div className="cur-flex cur-justify-center">
        <Progress progressType="circle" state="dark" />
      </div>
    );
  }

  return (
    <section className="-cur-mt-4 data-hj-suppress">
      <SmsLink
        brand={brand}
        setPhoneNumber={setPhoneNumber}
        setShowVerifyPhoneNumberModal={setShowVerifyPhoneNumberModal}
      />
      {photos.map((photo, index) => (
        <PhotoUploadAccordion
          brand={brand}
          index={index}
          key={photo.type}
          photo={photo}
          question={question}
          requiredPhotoTypes={requiredPhotoTypes}
          setShowPhotoIdModal={setShowPhotoIdModal}
          setSubmitButtonDisabled={setSubmitButtonDisabled}
          setUploadedFiles={setUploadedFiles}
          treatmentCategory={treatmentCategory}
          uploadedFiles={uploadedFiles}
        />
      ))}
      <div className="cur-mt-4">
        <Button
          className="cur-w-full"
          disabled={submitButtonDisabled}
          onClick={() => {
            trackEvent(AnalyticsEvent.PHOTO_UPLOAD_COMPLETED, {
              brandId,
              treatmentCategory: treatmentCategory!,
              treatmentCategoryId: TreatmentCategoryIds[treatmentCategory!],
              userId: patient?.id,
            });

            question.surveyModel.nextPage() ||
              question.surveyModel.completeLastPage();
          }}
        >
          {question.ctaButtonText ?? 'Send photos to provider'}
        </Button>
      </div>
      <VerifyPhoneNumberModal
        brand={brand}
        phoneNumber={phoneNumber}
        setPhoneNumber={setPhoneNumber}
        setShowVerifyPhoneNumberModal={setShowVerifyPhoneNumberModal}
        showVerifyPhoneNumberModal={showVerifyPhoneNumberModal}
      />
      <PhotoIdModal
        setShowPhotoIdModal={setShowPhotoIdModal}
        showPhotoIdModal={showPhotoIdModal}
      />
    </section>
  );
};

export default PhotoUpload;
